import { Dispatch, SetStateAction } from "react";
import { Debug } from "./debug";
import { fetchHost } from "./fetchutil";
import dv from "@datas/default.json";
import { DataProp, indexedDBProp, UserAuth } from "@/components/layout/input/inputType";
import { enLocale } from "@/i18n/routing";

const code_db_name = "CoffeeinSiteCode";
const code_db_version = 1;

const cache_db_name = "CoffeeinSiteCache";
const cache_db_version = 1;

//{ code_group_id: code_group_id, code_list_locale, access_at };
const codegroup_id = "code_group_id";
const codegroup_db_name = "code_group";
const codegroup_index_name = "by_code_groupid";
const code_cache_term = process.env.NEXT_PUBLIC_CACHE_TERM_LOW;
const codeDebug = false;
//{ url: data_url_str, user_seq: params.userSeq, result: data_result, access_at: now, access:true };

const getDBObjectStore = (
  db: IDBDatabase,
  store_name: string,
  mode: IDBTransactionMode,
  complete?: (this: IDBTransaction, ev: Event) => any
) => {
  const tx = db.transaction(store_name, mode);
  if (complete) {
    tx.oncomplete = complete;
  } else {
    tx.oncomplete = (ev: Event) => {
      Debug("oncomplete", db.name, store_name, mode, ev);
    };
  }
  return tx.objectStore(store_name);
};

const code_db_upgrade = (event: Event) => {
  if (event.target) {
    const db = (event.target as IDBOpenDBRequest).result;
    // Code용 스토어
    //{id:"",nick:"",img:"",last:"",access:"",ip:{ip:"",last:"", board:"",wr_id:"",c_id:""}}
    const codeGroupStore = db.createObjectStore(codegroup_db_name, { keyPath: codegroup_id });
    // Code용 인덱스
    const codeGroupIndex = codeGroupStore.createIndex(codegroup_index_name, codegroup_id);
  }
};

export const getCodeGroups = (
  groups: string[],
  locale?: string,
  setCodeGroups?: Dispatch<SetStateAction<Record<string, any[]> | undefined | null>>,
  complete?: Function,
  reload?: boolean
) => {
  if (window.indexedDB) {
    const check_time_diff = code_cache_term ? Number(code_cache_term) : 3600000; //3600000;
    const now = new Date();
    const check_time = new Date(now.getTime() - check_time_diff).valueOf();
    const code_db_request = indexedDB.open(code_db_name, code_db_version);
    code_db_request.onupgradeneeded = code_db_upgrade;
    code_db_request.onsuccess = function (event) {
      if (event.target) {
        const db = (event.target as IDBOpenDBRequest).result;
        const groups_string: string[] = [];
        const codeReadStore = getDBObjectStore(db, codegroup_db_name, "readonly", (ev) => {
          codeFetch(db, now, groups, groups_string, locale, setCodeGroups, complete);
        });
        //그룹코드 여부 조회 시작
        groups.forEach((group) => {
          const req = codeReadStore.get(group);
          req.onsuccess = (e) => {
            const result = (e.target as IDBRequest).result;
            if (!(reload ?? false) && result) {
              if (locale && locale === result.locale) {
                const access_at = new Date(result.access_at);
                if (access_at.valueOf() < check_time) {
                  const edit_at = new Date(result.edit_at);
                  groups_string.push(group + "|" + edit_at.toISOString());
                }
              } else {
                groups_string.push(group);
              }
            } else {
              groups_string.push(group);
            }
          };
        });
      }
    };
  }
};

const codeFetch = (
  db: IDBDatabase,
  now: Date,
  groups: string[],
  groups_string: string[],
  locale?: string,
  setCodeGroups?: Dispatch<SetStateAction<Record<string, any[]> | undefined | null>>,
  complete?: Function
) => {
  if (groups_string.length > 0 && navigator.onLine) {
    //갱신 그룹코드 요청
    fetchHost(
      "/api/code/simple",
      {
        ...dv.fetchOption,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code_group: groups_string }),
      },
      undefined,
      (data: any) => {
        codeRefresh(data, db, now, groups, groups_string, locale, setCodeGroups, complete);
      }
    );
  } else {
    codeRefresh(null, db, now, groups, groups_string, locale, setCodeGroups, complete);
  }
};

const sort_groups: string[] = ["CNTR", "VRT"];
//그룹코드 갱신 및 지정
const codeRefresh = (
  data: any,
  db: IDBDatabase,
  now: Date,
  groups: string[],
  groups_string: string[],
  locale?: string,
  setCodeGroups?: Dispatch<SetStateAction<Record<string, any[]> | undefined | null>>,
  complete?: Function
) => {
  const codeGroup: Record<string, any[]> = {};
  const codeWriteStore = getDBObjectStore(db, codegroup_db_name, "readwrite", (ev) => {
    if (setCodeGroups) {
      setCodeGroups(codeGroup);
    }
    if (complete) {
      complete(codeGroup);
    }
  });
  groups.forEach((data_group) => {
    const tempReq = codeWriteStore.get(data_group);
    tempReq.onsuccess = (e) => {
      if (e.target) {
        const result = (e.target as IDBRequest).result;
        const temp_data =
          data && data.result && data.result[data_group]
            ? {
                code_group_id: data_group,
                code_list: data.result[data_group],
                locale: locale,
                access_at: now.toISOString(),
                edit_at: data.result["edit_at"][data_group],
              }
            : result
            ? data
              ? { ...result, access_at: now.toISOString() }
              : result
            : undefined;
        //갱신 그룹코드 저장
        if (codeDebug) Debug(temp_data);
        if (temp_data) {
          if (result) {
            codeWriteStore.put(temp_data);
          } else {
            codeWriteStore.add(temp_data);
          }
          if (sort_groups.includes(data_group)) {
            const code_arr = temp_data.code_list;
            if (code_arr instanceof Array) {
              code_arr.sort(function (a, b) {
                if (locale === enLocale) {
                  return a.code_name.localeCompare(b.code_name);
                } else {
                  return a.code_name_kr.localeCompare(b.code_name_kr);
                }
              });
            }
            codeGroup[data_group] = code_arr;
          } else {
            codeGroup[data_group] = temp_data.code_list;
          }
        }
      }
    };
  });
};

//DBData 관련코드

const db_upgrade = (event: Event) => {
  if (event.target) {
    const db = (event.target as IDBOpenDBRequest).result;
    storeList.forEach((store) => {
      const store_prop = getStoreProp(store);
      if (store_prop) {
        const createStore = db.createObjectStore(store_prop.store, { keyPath: store_prop.index });
        const createIndex = createStore.createIndex(store_prop.indexName, store_prop.index);
        if (store_prop.exIndex) {
          Object.keys(store_prop.exIndex).forEach((key) => {
            if (store_prop.exIndex) {
              createStore.createIndex(key, store_prop.exIndex[key]);
            }
          });
        }
      }
    });
  }
};

//데이터 가져오기용 코드
const selectdata_id = "url";
const selectdata_db_name = "select_data";
const selectdata_index_name = "by_id";
const storeList = ["select_data"];
const getStoreProp = (store: string | boolean, term?: number): indexedDBProp | undefined => {
  if (store) {
    const db_version = 1;
    switch (store) {
      //version1
      case "code_group":
        return {
          name: code_db_name,
          version: code_db_version,
          store: "code_group",
          index: "code_group_id",
          indexName: "by_code_groupid",
          term: term ?? Number(process.env.NEXT_PUBLIC_CACHE_TERM_LOW),
        };
      case "select_data":
      default:
        return {
          name: cache_db_name,
          version: cache_db_version,
          store: "select_data",
          index: "url",
          indexName: "by_url",
          //term: term ?? Number(process.env.NEXT_PUBLIC_CACHE_TERM_MID),
          term: term ?? Number(process.env.NEXT_PUBLIC_CACHE_TERM_LOW),
        };
    }
  } else {
    undefined;
  }
};
export const getDBData = (
  field_name: string | undefined,
  user_seq: number | undefined,
  user_auth: UserAuth | undefined,
  locale: string | undefined,
  url: string,
  body: Record<string, any>,
  dataProp: DataProp | undefined,
  index: string | string[], //url
  now: Date | undefined,
  //store?: string,
  complete?: Function,
  delay?: boolean,
  reload?: boolean,
  count?: number
) => {
  const prop = getStoreProp(dataProp?.store ?? true, dataProp?.term);
  const check_now = now ?? new Date();
  let check_reload = reload ?? false;

  if (!(window.indexedDB && prop)) {
    dataFetch(
      undefined,
      undefined,
      field_name,
      user_seq,
      user_auth,
      locale,
      url,
      body,
      dataProp,
      index,
      check_now,
      complete,
      check_reload
    );
  } else {
    setTimeout(
      () => {
        const check_time_diff = prop.term;
        const check_time = new Date(check_now.getTime() - check_time_diff).valueOf();
        const db_request = indexedDB.open(prop.name, prop.version);
        db_request.onupgradeneeded = db_upgrade;
        db_request.onsuccess = function (event) {
          if (event.target) {
            let useFetch = false;
            let data: any = undefined;
            let retry = false;

            const db = (event.target as IDBOpenDBRequest).result;
            const dbStore = getDBObjectStore(db, prop.store, "readwrite", (ev) => {
              if (codeDebug) Debug(field_name, url, index, prop, useFetch, check_reload);
              if (useFetch) {
                dataFetch(
                  db,
                  prop.store,
                  field_name,
                  user_seq,
                  user_auth,
                  locale,
                  url,
                  body,
                  dataProp,
                  index,
                  check_now,
                  complete,
                  check_reload
                );
                //codeFetch(db, now, groups, groups_string, locale, setCodeGroups, complete);
              } else {
                if (retry && (count ?? 0) < 5) {
                  setTimeout(() => {
                    getDBData(
                      field_name,
                      user_seq,
                      user_auth,
                      locale,
                      url,
                      body,
                      dataProp,
                      index,
                      now,
                      complete,
                      false,
                      reload,
                      (count ?? 0) + 1
                    );
                  }, 100);
                } else {
                  if (complete && data) {
                    complete(data);
                  }
                }
              }
            });
            const req = dbStore.get(index);
            req.onsuccess = (e) => {
              const result = (e.target as IDBRequest).result;
              if (codeDebug) Debug(field_name, result);
              if (result) {
                data = result;
                const access_at = new Date(result.access_at);
                const data_user = result.user_seq;
                if (reload || data_user !== user_seq || (!result.access && access_at.valueOf() < check_time)) {
                  if (data_user !== user_seq) check_reload = true;
                  result.access = true;
                  useFetch = true;
                  dbStore.put(result);
                } else if (result.access) {
                  retry = true;
                }
              } else {
                const temp_result: any = {
                  url: index,
                  user_seq: user_seq,
                  result: undefined,
                  access_at: now,
                  access: true,
                };
                if (typeof prop.index === "string") {
                  temp_result[prop.index] = index.toString();
                } else {
                  prop.index.forEach((item, i) => {
                    if (typeof index === "string") {
                      temp_result[item] = index;
                    } else if (index[i] !== undefined && index[i] !== null) {
                      temp_result[item] = index[i];
                    }
                  });
                }
                useFetch = true;
                check_reload = true;
                dbStore.add(temp_result);
              }
            };
          }
        };
      },
      delay ? 50 : 0
    );
  }
};

const dataFetch = (
  db: IDBDatabase | undefined,
  store: string | undefined,
  field_name: string | undefined,
  user_seq: number | undefined,
  user_auth: UserAuth | undefined,
  locale: string | undefined,
  url: string,
  body: Record<string, any>,
  dataProp: DataProp | undefined,
  index: string | string[], //url+params
  now: Date,
  //store?: string,
  complete?: Function,
  reload?: boolean
) => {
  const fetch_option = dataProp?.option ?? dv.fetchPostOption;
  if (!reload) {
    body["last_edit_at"] = now.toISOString();
  }
  if (codeDebug) Debug("dataFetch option :" + field_name, url, fetch_option, body, user_auth);
  fetchHost(
    url,
    { ...fetch_option, body: JSON.stringify(body) },
    user_auth,
    (data: any) => {
      const data_result = dataProp?.process ? dataProp.process(data) : data.result;
      const result =
        !reload && (!data_result || (data_result instanceof Array && data_result.length === 0))
          ? undefined
          : data_result;
      const put_result = { url: index, user_seq: user_seq, result, access_at: now, access: false };
      if (codeDebug) Debug("dataFetch fetchHost :" + field_name, dataProp, data.result, data_result, result);

      if (db && store) {
        const writeStore = getDBObjectStore(db, store, "readwrite", (ev) => {
          if (complete) {
            complete(put_result);
          }
        });
        const tempReq = writeStore.get(index);
        tempReq.onsuccess = (e) => {
          if (e.target) {
            const db_result = (e.target as IDBRequest).result;
            if (db_result) {
              if (!reload && !result) {
                db_result.access = false;
                db_result.access_at = now;
                put_result.result = db_result.result;
                writeStore.put(db_result);
              } else {
                writeStore.put(put_result);
              }
            } else {
              Debug(index, put_result, db_result, e.target);
              writeStore.add(put_result);
            }
          }
        };
      } else {
        if (complete) {
          complete(put_result);
        }
      }
    },
    (e: any) => {
      if (dataProp?.error) {
        dataProp.error(e);
      }
    }
  );
};
export const clearIndexedDB = (exCord?: boolean) => {
  if (window.indexedDB) {
    window.indexedDB.databases().then(function (databases) {
      if (databases.length > 0) {
        databases.forEach((database) => {
          if (database.name && (!exCord || (exCord && database.name !== "CoffeeinSiteCode"))) {
            window.indexedDB.deleteDatabase(database.name);
          }
        });
      }
    });
  }
};
